import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { lightTheme, darkTheme } from './theme';
import useAuthStore from '../store/authStore';

const STORAGE_KEY = 'stacktrack-color-mode';

const ColorModeContext = createContext({
  mode: 'light',
  preference: 'system',
  setPreference: () => {},
  setMode: () => {},
  toggleColorMode: () => {},
});

export function useColorMode() {
  return useContext(ColorModeContext);
}

function getSystemModeSnapshot() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** @returns {'light' | 'dark' | 'system'} */
function getInitialPreference() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* ignore */
  }
  return 'system';
}

const THEME_SYNC_AFTER_LOGIN_KEY = 'stacktrack-theme-sync-system';

/**
 * True when the user has chosen a fixed light/dark theme (not "follow OS").
 * Legacy: missing key meant "follow system" — treat like `system`.
 */
function hasExplicitColorPreference() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark';
  } catch {
    return false;
  }
}

export function AppThemeProvider({ children }) {
  const user = useAuthStore((s) => s.user);
  const [systemMode, setSystemMode] = useState(() => getSystemModeSnapshot());
  const [preference, setPreferenceState] = useState(getInitialPreference);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setSystemMode(mq.matches ? 'dark' : 'light');
    handler();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  useEffect(() => {
    if (!user) return;
    try {
      if (sessionStorage.getItem(THEME_SYNC_AFTER_LOGIN_KEY) !== '1') return;
      sessionStorage.removeItem(THEME_SYNC_AFTER_LOGIN_KEY);
      if (hasExplicitColorPreference()) return;
    } catch {
      return;
    }
    setPreferenceState('system');
  }, [user]);

  useEffect(() => {
    if (preference !== 'system') return;
    const sync = () => setSystemMode(getSystemModeSnapshot());
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [preference]);

  const effectiveMode = preference === 'system' ? systemMode : preference;

  const setPreference = useCallback((next) => {
    setPreferenceState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      if (resolved !== 'light' && resolved !== 'dark' && resolved !== 'system') return prev;
      try {
        localStorage.setItem(STORAGE_KEY, resolved);
      } catch {
        /* ignore */
      }
      return resolved;
    });
  }, []);

  const setMode = useCallback(
    (next) => {
      setPreferenceState((prev) => {
        const base = prev === 'system' ? systemMode : prev;
        const resolved = typeof next === 'function' ? next(base) : next;
        if (resolved !== 'light' && resolved !== 'dark') return prev;
        try {
          localStorage.setItem(STORAGE_KEY, resolved);
        } catch {
          /* ignore */
        }
        return resolved;
      });
    },
    [systemMode],
  );

  const toggleColorMode = useCallback(() => {
    setPreferenceState((prev) => {
      const effective = prev === 'system' ? systemMode : prev;
      const next = effective === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [systemMode]);

  const theme = useMemo(() => (effectiveMode === 'dark' ? darkTheme : lightTheme), [effectiveMode]);

  const value = useMemo(
    () => ({ mode: effectiveMode, preference, setPreference, setMode, toggleColorMode }),
    [effectiveMode, preference, setPreference, setMode, toggleColorMode],
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
